import { APPUSERROLES, getUserData } from '@/constants/global';
import { useOrganizationContext } from '@/context/organization';

export const CANCELLATION_READ_ROLES = [APPUSERROLES.SUPERADMIN, APPUSERROLES.PARTYADMIN, APPUSERROLES.CANDIDATE];
export const CANCELLATION_WRITE_ROLES = [APPUSERROLES.SUPERADMIN, APPUSERROLES.PARTYADMIN];

/** Read live: the exported role constants are evaluated once at import. The WHOLE array counts. */
export const currentRoles = (): string[] => {
	const roles = getUserData().user?.role;
	return (Array.isArray(roles) ? roles : [roles || '']).filter(Boolean);
};

export interface ICancellationScope {
	electionId: number;
	electionName: string;
	/** Sent on the URL ONLY for Super Admin; everyone else's party comes from the token. */
	partyId: number | null;
	/** The party this view is about — for cache keys and per-party storage, never sent. */
	partyKey: number;
	isSuperAdmin: boolean;
	/** Super Admin / Party Admin / Candidate, from any role in the list. */
	canRead: boolean;
	/** Super Admin / Party Admin — Confirm, Reinstate, bulk entry, alert settings. */
	canWrite: boolean;
	/** False for a Super Admin who has not picked a party: a different state from "no data". */
	partyResolved: boolean;
}

export const resolveScope = (args: {
	roles: string[];
	electionId: unknown;
	electionName?: string;
	orgPartyId: unknown;
	tokenPartyId: unknown;
}): ICancellationScope => {
	const { roles } = args;
	const isSuperAdmin = roles.includes(APPUSERROLES.SUPERADMIN);
	const canRead = roles.some((r) => CANCELLATION_READ_ROLES.includes(r));
	const canWrite = roles.some((r) => CANCELLATION_WRITE_ROLES.includes(r));
	const electionId = Number(args.electionId) || 0;
	const orgParty = Number(args.orgPartyId);
	const tokenParty = Number(args.tokenPartyId);
	const orgPartyValid = Number.isFinite(orgParty) && orgParty > 0;
	const tokenPartyValid = Number.isFinite(tokenParty) && tokenParty > 0;
	const superAdminParty = orgPartyValid ? orgParty : null;
	const partyKey = isSuperAdmin ? (superAdminParty ?? 0) : orgPartyValid ? orgParty : tokenPartyValid ? tokenParty : 0;
	return {
		electionId,
		electionName: args.electionName || 'Election',
		partyId: isSuperAdmin ? superAdminParty : null,
		partyKey,
		isSuperAdmin,
		canRead,
		canWrite,
		partyResolved: isSuperAdmin ? superAdminParty !== null : partyKey > 0,
	};
};

export const useCancellationScope = (): ICancellationScope => {
	const organization = useOrganizationContext();
	return resolveScope({
		roles: currentRoles(),
		electionId: organization.selectedElection?.electionId,
		electionName: organization.selectedElection?.electionName,
		orgPartyId: organization.id,
		tokenPartyId: getUserData().user?.PartyId,
	});
};
